import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { cn } from "../utils/cn";

export function Button({
  className,
  variant = "primary",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "ghost" | "danger" | "outline" }) {
  const styles = {
    primary: "bg-accent text-white hover:bg-accent/90",
    ghost: "bg-white/[0.04] hover:bg-white/[0.08] text-zinc-100",
    outline: "border border-white/10 hover:border-white/20 hover:bg-white/[0.03]",
    danger: "bg-rose-700 hover:bg-rose-600 text-white",
  };
  return (
    <button
      className={cn(
        "focus-ring inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition disabled:opacity-50",
        styles[variant],
        className,
      )}
      {...props}
    />
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        "focus-ring w-full rounded-md border border-white/10 bg-ink-950 px-3 py-2 text-sm text-white placeholder:text-zinc-500",
        className,
      )}
      {...props}
    />
  );
});

export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "focus-ring w-full min-h-10 rounded-md border border-white/10 bg-ink-950 px-3 py-2 text-sm text-white",
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        "focus-ring w-full rounded-md border border-white/10 bg-ink-950 px-3 py-2 text-sm text-white placeholder:text-zinc-500",
        className,
      )}
      {...props}
    />
  );
}

export function RoleIdList({
  label,
  hint,
  values,
  onChange,
}: {
  label: string;
  hint?: string;
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const ids = values.length ? values : [""];
  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium text-zinc-200">{label}</span>
      <div className="space-y-2">
        {ids.map((id, index) => (
          <div key={index} className="flex gap-2">
            <Input
              inputMode="numeric"
              placeholder="Discord role ID"
              value={id}
              onChange={(e) => {
                const next = [...ids];
                next[index] = e.target.value;
                onChange(next);
              }}
              autoComplete="off"
            />
            <Button
              type="button"
              variant="ghost"
              className="shrink-0 px-3"
              disabled={ids.length === 1 && !ids[0]}
              onClick={() => onChange(ids.filter((_, item) => item !== index))}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => onChange([...ids, ""])}>
          Add role ID
        </Button>
      </div>
      {hint ? <span className="block text-xs leading-relaxed text-zinc-500">{hint}</span> : null}
    </div>
  );
}

export function Field({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block space-y-1.5">
      <span className="text-sm font-medium text-zinc-200">{label}</span>
      {children}
      {hint ? <span className="block text-xs leading-relaxed text-zinc-500">{hint}</span> : null}
    </label>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn("rounded-lg border border-white/10 bg-ink-900 p-4 sm:p-5", className)}>{children}</section>;
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-zinc-400">{subtitle}</p> : null}
      </div>
      {actions}
    </div>
  );
}

export function StatusPill({ status }: { status: "ACTIVE" | "INACTIVE" }) {
  const active = status === "ACTIVE";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
        active ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300",
      )}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export function TeamBadge({ team }: { team: { name: string; color: string } }) {
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
      style={{ background: `${team.color}22`, color: team.color }}
    >
      {team.name}
    </span>
  );
}

export function EmptyState({ title, body }: { title: string; body?: string }) {
  return (
    <div className="rounded-lg border border-dashed border-white/10 px-6 py-10 text-center">
      <p className="text-base font-medium">{title}</p>
      {body ? <p className="mt-2 text-sm text-zinc-400">{body}</p> : null}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-md bg-white/5", className)} />;
}
